import type { CalendarConnection } from "../../domain/app-types.ts";
import {
  loadCalendarConnections,
  reconcileExternalCalendars,
  refreshExternalCalendarContext,
  type ExternalCalendarContextCapabilities,
} from "./context-controller.ts";
import { openCalendarConnections } from "./connections-controller.ts";
import { openCalendarFeed } from "./feed-controller.ts";

export interface CalendarIntegrationWorkflowCapabilities extends Omit<ExternalCalendarContextCapabilities, "connections"> {
  connections(): CalendarConnection[];
  setConnections(connections: CalendarConnection[]): void;
  activeRoot(): string | null;
  activeRootName(): string;
  isTaskOnlyRoot(rootId: string): boolean;
  apiError(response: Response, fallback: string): Promise<string>;
  confirmDisconnect(): Promise<boolean>;
  confirmRevoke(): Promise<boolean>;
  navigate(url: string): void;
  copy(value: string): Promise<void>;
  origin(): string;
  icon(name: string): string;
  toast(message: string, error?: boolean): void;
  renderShell(): void | Promise<void>;
}

export function createCalendarIntegrationWorkflow(capabilities: CalendarIntegrationWorkflowCapabilities) {
  async function refreshConnections(requireServer = false): Promise<void> {
    const connections = await loadCalendarConnections(capabilities.request);
    if (connections) capabilities.setConnections(connections);
    else if (requireServer) throw new Error("Calendar connections could not refresh");
  }

  function refreshContext(): Promise<void> {
    return refreshExternalCalendarContext(capabilities);
  }

  function reconcile(): Promise<number | null> {
    return reconcileExternalCalendars(capabilities.connections(), capabilities.online(), capabilities.request);
  }

  async function openFeed(): Promise<void> {
    const rootId = capabilities.activeRoot();
    if (!rootId || capabilities.isTaskOnlyRoot(rootId)) {
      capabilities.toast("Calendar subscriptions require space membership", true);
      return;
    }
    await openCalendarFeed(rootId, capabilities.activeRootName(), {
      request: capabilities.request,
      apiError: capabilities.apiError,
      confirmRevoke: capabilities.confirmRevoke,
      copy: capabilities.copy,
      origin: capabilities.origin,
      icon: capabilities.icon,
      toast: capabilities.toast,
    });
  }

  async function openConnections(): Promise<void> {
    await openCalendarConnections({
      connections: capabilities.connections,
      refresh: () => refreshConnections(true),
      reconcile,
      request: capabilities.request,
      apiError: capabilities.apiError,
      confirmDisconnect: capabilities.confirmDisconnect,
      navigate: capabilities.navigate,
      icon: capabilities.icon,
      toast: capabilities.toast,
      renderShell: capabilities.renderShell,
    });
  }

  return { refreshConnections, refreshContext, reconcile, openFeed, openConnections };
}
